// 0.W Settings changes from the product's forms (docs/specs/P0_self_serve_onboarding.md, step 7 "Your settings").
// A copy of supabase/functions/act/lib/settings.ts (the act function's checks), used by the forms to say what is
// wrong before saving and by demo mode to refuse what act refuses. settingsRules.test.ts checks the two stay the same.
// One generic action, propose_settings_change, takes a section key and the fields that change. Each section has an
// allowed list of fields, checked here and again in the database (crm__settings_fields, migrations 20261110140000 and
// 20261122090000), so a form can never touch human_gates, budgets, compliance or anything not on the list.

export class SettingsRefusal extends Error {}

type Check = (v: unknown, field: string) => unknown;

function no(field: string, what: string): never {
  throw new SettingsRefusal(`${field} ${what}`);
}

const bool: Check = (v, f) => (typeof v === "boolean" ? v : no(f, "must be true or false"));

const int = (min: number, max: number): Check => (v, f) =>
  typeof v === "number" && Number.isInteger(v) && v >= min && v <= max ? v : no(f, `must be a whole number from ${min} to ${max}`);

const amount = (min: number, max: number): Check => (v, f) =>
  typeof v === "number" && Number.isFinite(v) && v >= min && v <= max ? Math.round(v * 100) / 100 : no(f, `must be an amount from ${min} to ${max.toLocaleString("en-GB")}`);

const text = (max: number, re?: RegExp, what = "is not in the right form"): Check => (v, f) => {
  if (typeof v !== "string") no(f, "must be text");
  const s = v.trim();
  if (s.length > max) no(f, `must be at most ${max} characters`);
  if (re && !re.test(s)) no(f, what);
  return s;
};

const HTTPS_RE = /^https:\/\/[^\s<>"']+$/;
/** An https address, or "" to clear it. */
const url: Check = (v, f) => {
  const s = text(500)(v, f) as string;
  if (s && !HTTPS_RE.test(s)) no(f, "must be a web address starting with https://");
  return s;
};

const oneOf = (values: readonly string[]): Check => (v, f) =>
  typeof v === "string" && values.includes(v) ? v : no(f, `must be one of ${values.join(", ")}`);

/** A list of short texts: trimmed, blanks dropped, duplicates removed. */
const list = (maxItems: number, item: Check): Check => (v, f) => {
  if (!Array.isArray(v)) no(f, "must be a list");
  const out = [...new Set(v.map((x, i) => item(x, `${f}[${i}]`) as string).filter((x) => x !== ""))];
  if (out.length > maxItems) no(f, `may list at most ${maxItems}`);
  return out;
};

function plain(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

export const OFFERS = ["consult", "quote", "guide", "scorecard", "subscribe", "visit", "call"] as const;
export const CTA_KINDS = ["link", "keyword", "qr", "phone", "form"] as const;
export const ROUTES = ["enquiry", "book", "dm", "call", "subscribe", "download", "review"] as const;
export const FIRST_STEPS = ["6.R", "7.R", "10.1", "human", "none"] as const;
export const NETWORKS = ["instagram", "facebook", "x", "linkedin", "tiktok", "pinterest", "threads", "youtube", "google_business_profile"] as const;
export const SYNDICATE = ["linkedin", "substack", "medium", "trade_press"] as const;
export const PUBLISH_TO = ["ogma_pages", "subfolder_worker", "subdomain", "wordpress", "webflow", "shopify", "wix", "ghost"] as const;
export const AD_PLATFORMS = ["meta", "google", "microsoft", "nextdoor", "linkedin"] as const;
const HOST_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
const TIME_RE = /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun) ([01][0-9]|2[0-3]):[0-5][0-9]$/;

/** One CTA in the starting library (crm_upsert_cta makes the code and checks the destination is the business's own). */
const ctaEntry: Check = (v, f) => {
  if (!plain(v)) no(f, "must be an offer with a label");
  for (const k of Object.keys(v)) if (!["offer", "kind", "label", "destination", "keyword", "phone", "channel"].includes(k)) no(`${f}.${k}`, "is not accepted here");
  const out: Record<string, unknown> = {
    offer: oneOf(OFFERS)(v.offer, `${f}.offer`),
    kind: oneOf(CTA_KINDS)(v.kind, `${f}.kind`),
    label: text(120, /^.{2,}$/, "must be at least 2 characters")(v.label, `${f}.label`),
  };
  const opt = (k: string, check: Check) => {
    if (v[k] === undefined || v[k] === null || v[k] === "") return;
    out[k] = check(v[k], `${f}.${k}`);
  };
  opt("destination", url);
  opt("keyword", (x, ff) => text(20, /^[A-Z0-9]{2,20}$/, "must be 2 to 20 letters or numbers")(typeof x === "string" ? x.toUpperCase() : x, ff));
  opt("phone", text(20, /^\+?[0-9 ()-]{6,20}$/, "must be a phone number"));
  opt("channel", text(30, /^[a-z_]{2,30}$/, "must be a channel key like instagram"));
  if (["link", "qr", "form"].includes(out.kind as string) && !out.destination) no(`${f}.destination`, "is needed for a link, QR code or form");
  if (out.kind === "keyword" && !out.keyword) no(`${f}.keyword`, "is needed for a keyword");
  if (out.kind === "phone" && !out.phone) no(`${f}.phone`, "is needed for a phone number");
  return out;
};

/** Who posts for a social account (0.12, docs/specs/META_DIRECT.md): Buffer (the default when absent) or Meta direct. */
export const PUBLISHERS = ["buffer", "meta"] as const;
const META_ID_RE = /^[0-9]{5,25}$/;

/**
 * One social account, as the Buffer picker, the Meta picker or the Social form records it. With publisher meta, a
 * Facebook entry needs its Page id (meta_page_id) and an Instagram entry its Instagram user id (ig_user_id); no Buffer
 * channel is needed then. Meta posts only to Instagram and Facebook.
 */
const networkEntry: Check = (v, f) => {
  if (!plain(v)) no(f, "must be a network and an account");
  for (const k of Object.keys(v)) if (!["network", "account", "buffer_channel_id", "cadence_per_week", "name", "publisher", "meta_page_id", "ig_user_id"].includes(k)) no(`${f}.${k}`, "is not accepted here");
  const out: Record<string, unknown> = {
    network: oneOf(NETWORKS)(v.network, `${f}.network`),
    account: text(30, /^[a-z_]{2,30}$/, "must be a short account kind like business or page")(v.account, `${f}.account`),
  };
  if (v.buffer_channel_id !== undefined && v.buffer_channel_id !== null && v.buffer_channel_id !== "") {
    out.buffer_channel_id = text(64, /^[A-Za-z0-9_-]{1,64}$/, "must be a Buffer channel id")(v.buffer_channel_id, `${f}.buffer_channel_id`);
  }
  if (v.cadence_per_week !== undefined && v.cadence_per_week !== null) out.cadence_per_week = int(0, 21)(v.cadence_per_week, `${f}.cadence_per_week`);
  if (v.name !== undefined && v.name !== null && v.name !== "") out.name = text(120)(v.name, `${f}.name`);
  if (v.publisher !== undefined && v.publisher !== null && v.publisher !== "") out.publisher = oneOf(PUBLISHERS)(v.publisher, `${f}.publisher`);
  for (const k of ["meta_page_id", "ig_user_id"]) {
    if (v[k] !== undefined && v[k] !== null && v[k] !== "") out[k] = text(25, META_ID_RE, "must be a Meta id (digits)")(v[k], `${f}.${k}`);
  }
  if (out.publisher === "meta") {
    if (out.network === "facebook" && !out.meta_page_id) no(`${f}.meta_page_id`, "is needed to post to a Facebook Page through Meta");
    if (out.network === "instagram" && !out.ig_user_id) no(`${f}.ig_user_id`, "is needed to post to Instagram through Meta");
    if (out.network !== "facebook" && out.network !== "instagram") no(`${f}.publisher`, "can be meta only for Instagram or Facebook");
  }
  return out;
};

const networks: Check = (v, f) => {
  const out = list(12, networkEntry)(v, f) as unknown as Record<string, unknown>[];
  const seen = new Set<string>();
  for (const n of out) {
    const k = `${n.network}/${n.account}`;
    if (seen.has(k)) no(f, `lists ${k} twice`);
    seen.add(k);
  }
  return out;
};

const bestTimes: Check = (v, f) => {
  if (!plain(v)) no(f, "must be times per network");
  const out: Record<string, string[]> = {};
  for (const [k, times] of Object.entries(v)) {
    oneOf(NETWORKS)(k, `${f} network`);
    out[k] = list(14, text(9, TIME_RE, "must be a time like Mon 18:00"))(times, `${f}.${k}`) as string[];
  }
  return out;
};

const reviewLinks: Check = (v, f) => {
  if (!plain(v)) no(f, "must be a link per review site");
  const entries = Object.entries(v);
  if (entries.length > 10) no(f, "may have at most 10 sites");
  const out: Record<string, string> = {};
  for (const [k, link] of entries) {
    if (!/^[a-z0-9_]{1,40}$/.test(k)) no(f, `cannot have the site ${k.slice(0, 40)}`);
    const s = url(link, `${f}.${k}`) as string;
    if (!s) no(`${f}.${k}`, "must be a web address starting with https://");
    out[k] = s;
  }
  return out;
};

/** 4.3, 4.21 When cold emails go out (migration 20261126090000; engine/outreach.py reads the same). */
export const SEND_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
export const SEND_DAY_SETS = ["weekdays", "weekends", "all"] as const;
const HHMM_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
const SLOT_NAME_RE = /^[a-z][a-z0-9_]{1,29}$/;
const ZONE_RE = /^(UTC|[A-Za-z_]+(\/[A-Za-z0-9_+-]+){1,2})$/;

/** A time zone the engine knows: UTC or an IANA name like Europe/London. */
export function zoneOk(z: string): boolean {
  if (!ZONE_RE.test(z)) return false;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: z });
    return true;
  } catch {
    return false;
  }
}

/** One window: days (weekdays, weekends, all or a list of Mon to Sun), start and end as HH:MM (an end before the start
 * crosses midnight and belongs to the day it starts), and a name when it is a send-time test slot. */
const sendWindowEntry = (named: boolean): Check => (v, f) => {
  if (!plain(v)) no(f, "must be a window with days, start and end");
  for (const k of Object.keys(v)) if (!["days", "start", "end", ...(named ? ["name"] : [])].includes(k)) no(`${f}.${k}`, "is not accepted here");
  let days: unknown;
  if (typeof v.days === "string") days = oneOf(SEND_DAY_SETS)(v.days, `${f}.days`);
  else if (Array.isArray(v.days) && v.days.length >= 1 && v.days.length <= 7) {
    const picked = v.days.map((d, i) => oneOf(SEND_DAYS)(d, `${f}.days[${i}]`) as string);
    if (new Set(picked).size !== picked.length) no(`${f}.days`, "lists a day twice");
    days = SEND_DAYS.filter((d) => picked.includes(d));
  } else no(`${f}.days`, "must be weekdays, weekends, all or a list of days");
  const start = text(5, HHMM_RE, "must be a time like 07:30")(v.start, `${f}.start`);
  const end = text(5, HHMM_RE, "must be a time like 07:30")(v.end, `${f}.end`);
  if (start === end) no(f, "starts and ends at the same time");
  const out: Record<string, unknown> = { days, start, end };
  return named ? { name: text(30, SLOT_NAME_RE, "must be 2 to 30 lower-case letters, numbers or _, starting with a letter")(v.name, `${f}.name`), ...out } : out;
};

/** channels.email.send_window: 1 to 6 windows, and whose time zone they are in (recipient, business or a zone). */
const sendWindow: Check = (v, f) => {
  if (!plain(v)) no(f, "must be the windows emails go out in");
  for (const k of Object.keys(v)) if (!["windows", "timezone", "timezones"].includes(k)) no(`${f}.${k}`, "is not accepted here");
  if (!Array.isArray(v.windows) || v.windows.length < 1 || v.windows.length > 6) no(`${f}.windows`, "must have 1 to 6 windows");
  const out: Record<string, unknown> = { windows: v.windows.map((w, i) => sendWindowEntry(false)(w, `${f}.windows[${i}]`)) };
  if (v.timezone !== undefined) {
    const tz = text(64)(v.timezone, `${f}.timezone`) as string;
    if (tz !== "recipient" && tz !== "business" && !zoneOk(tz)) no(`${f}.timezone`, "must be recipient, business or a zone like Europe/London");
    out.timezone = tz;
  }
  if (v.timezones !== undefined) {
    if (!plain(v.timezones)) no(`${f}.timezones`, "must be a zone per country");
    const zones: Record<string, string> = {};
    const entries = Object.entries(v.timezones);
    if (entries.length > 30) no(`${f}.timezones`, "may list at most 30 countries");
    for (const [c, z] of entries) {
      if (!/^[A-Z]{2}$/.test(c)) no(`${f}.timezones`, `cannot have the country ${c.slice(0, 10)}`);
      const zone = text(64)(z, `${f}.timezones.${c}`) as string;
      if (!zoneOk(zone)) no(`${f}.timezones.${c}`, "must be a zone like America/Chicago");
      zones[c] = zone;
    }
    out.timezones = zones;
  }
  return out;
};

/** 4.21 channels.email.send_time_test: on or off, and the two named windows it compares. */
const sendTimeTest: Check = (v, f) => {
  if (!plain(v)) no(f, "must say whether the test is on and which two windows it compares");
  for (const k of Object.keys(v)) if (!["enabled", "slots"].includes(k)) no(`${f}.${k}`, "is not accepted here");
  const out: Record<string, unknown> = { enabled: bool(v.enabled, `${f}.enabled`) };
  const given = Array.isArray(v.slots) ? v.slots.length > 0 : v.slots !== undefined && v.slots !== null;
  if (out.enabled === true || given) {
    if (!Array.isArray(v.slots) || v.slots.length !== 2) no(`${f}.slots`, "must be exactly two windows");
    const slots = v.slots.map((s, i) => sendWindowEntry(true)(s, `${f}.slots[${i}]`) as Record<string, unknown>);
    if (slots[0]!.name === slots[1]!.name) no(`${f}.slots`, "need different names");
    out.slots = slots;
  } else if (Array.isArray(v.slots)) out.slots = [];
  return out;
};

const firstStep = oneOf(FIRST_STEPS);

/** 1.61, E56 speed to lead (docs/specs/SPEED_TO_LEAD.md section 8; migration 20261125090000). */
export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
/** A time range a day, "09:00-17:00" (scheduling.working_hours, engine/scheduler.py). */
const HOUR_RANGE_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]-([01][0-9]|2[0-3]):[0-5][0-9]$/;
/** What a meeting brief can hold (engine/meetings.py BRIEF_SECTIONS). */
export const BRIEF_SECTIONS = ["company", "contact", "offer", "outreach", "recent_meetings", "talking_points", "numbers", "ownership", "news", "questions"] as const;
export const INSTANT_REPLY_KINDS = ["enquiry", "scorecard", "chat"] as const;
/** 1.61 The order the instant reply tries its channels (migration 20261127090000, kind "choice"): WhatsApp with email as
 * the fallback, WhatsApp only, or email only. Exactly one of these lists, in this order. */
export const INSTANT_REPLY_CHANNELS = [["whatsapp", "email"], ["whatsapp"], ["email"]] as const;
const channelOrder: Check = (v, f) => {
  const found = Array.isArray(v) ? INSTANT_REPLY_CHANNELS.find((c) => c.length === v.length && c.every((x, i) => v[i] === x)) : undefined;
  return found ? [...found] : no(f, "must be WhatsApp then email, WhatsApp only, or email only");
};
/** 1.61 review R2: the countries the WhatsApp instant reply may go to, as calling codes ("44", "353", "1"), no plus sign. */
const callingCode = text(3, /^([1-9][0-9]{0,2})?$/, "must be a country calling code like 44, without the +");
/** 1.61 review R4: the key of the Tally checkbox field that is the WhatsApp tick box ("" = none). */
const tallyField = text(100, /^[A-Za-z0-9_-]*$/, "must be a Tally field key (letters, numbers, _ or -)");
/** Bot check (Cloudflare Turnstile): the widget's public site key ("" = off). The secret key stays in the vault. */
const siteKey = text(100, /^[A-Za-z0-9_-]*$/, "must be a Turnstile site key (letters, numbers, _ or -)");
/** The merge fields the instant reply may use; all are filled from the CRM, none from the enquiry. */
export const MERGE_FIELDS = ["first_name", "owner_name", "business", "booking_link", "reply_by"] as const;
const clock = text(5, HHMM_RE, "must be a time like 08:00");

/** The instant reply's wording: plain text, not empty, only the known merge fields, and one line for a subject. */
const wording = (min: number, max: number, oneLine = false): Check => (v, f) => {
  const s = text(max)(v, f) as string;
  if (s.length < min) no(f, `must be at least ${min} characters`);
  if (oneLine && /[\r\n]/.test(s)) no(f, "must be one line");
  for (const m of s.matchAll(/\{([^{}]*)\}/g)) {
    if (!(MERGE_FIELDS as readonly string[]).includes(m[1]!)) no(f, `cannot use {${m[1]!.slice(0, 30)}}: the merge fields are ${MERGE_FIELDS.map((x) => `{${x}}`).join(", ")}`);
  }
  return s;
};

/** Connect paths (docs/specs/CONNECT.md section 4.1; crm__connect_templates, migration 20261122090000): the blocks each
 * template may use, in order, and which of them can be switched off. */
export const PATH_TEMPLATES: Record<string, { blocks: readonly string[]; optional: readonly string[] }> = {
  intro_call: { blocks: ["qualify", "book", "meet", "recap", "proposal", "accept", "follow_up"], optional: ["qualify", "proposal", "accept"] },
  instant_price: { blocks: ["qualify", "quote", "accept", "pay", "follow_up"], optional: ["qualify", "pay"] },
  quote_from_known: { blocks: ["gather", "quote", "accept", "pay", "follow_up"], optional: ["pay"] },
  survey_then_quote: { blocks: ["book", "meet", "quote", "accept", "pay", "follow_up"], optional: ["pay"] },
  trial_access: { blocks: ["sign_up", "grant_access", "nudge", "convert", "follow_up"], optional: [] },
  tender_response: { blocks: ["bid_decision", "prepare", "submit", "follow_up"], optional: [] },
};
/** Every step a path can have (crm__connect_blocks), for a path's own steps: the templates' and the steps no template
 * has, Send a document and Make an offer (migration 20270305100000). */
export const PATH_BLOCKS = [...new Set([...Object.values(PATH_TEMPLATES).flatMap((t) => t.blocks), "send_document", "make_offer"])];
/** crm__path_order_problem: follow up comes last, and in each pair the first comes before the second when both are on. */
const PATH_ORDER_RULES: readonly (readonly [string, string])[] = [
  ["book", "meet"], ["meet", "recap"], ["quote", "accept"], ["proposal", "accept"], ["accept", "pay"],
  ["sign_up", "grant_access"], ["sign_up", "nudge"], ["sign_up", "convert"],
];
export function pathOrderProblem(blocks: readonly string[]): string | null {
  const pos = new Map(blocks.map((b, i) => [b, i]));
  if (pos.has("follow_up") && pos.get("follow_up") !== blocks.length - 1) return "Follow up must be the last step";
  for (const [before, after] of PATH_ORDER_RULES) {
    const b = pos.get(before);
    const a = pos.get(after);
    if (b !== undefined && a !== undefined && b > a) return `${after} must come after ${before}`;
  }
  return null;
}
export const FLAVOURS = ["attract", "find", "network"] as const;
export const FIRST_ASKS = ["book", "quote", "call", "coffee"] as const;
export const OFFER_AFTER = ["first_reply", "meeting", "relationship"] as const;
export const SAVE_OFFER_KINDS = ["discount", "smaller_first_job", "payment_terms", "trial"] as const;
export const SAVE_OFFER_APPROVAL = ["always", "never", "above"] as const;
const PATH_KEY_RE = /^[a-z][a-z0-9_]{1,40}$/;

/** A campaign on a path (connect.paths[].campaigns, migration 20261229090000): a campaign id, sent in lower case. */
const CAMPAIGN_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const campaignId: Check = (v, f) => (typeof v === "string" && CAMPAIGN_ID_RE.test(v.trim()) ? v.trim().toLowerCase() : no(f, "must be a campaign id"));

/** Step settings on a path (connect.paths[].steps; migration 20261231090000): what each step may hold. "reply" is the
 * first reply to an enquiry; any other block of the path takes due_hours only. */
export const STEP_MODES: Record<string, readonly string[]> = {
  reply: ["acknowledge", "booking_link", "offer_times", "confirm_booking", "send_guide"],
  book: ["send_link", "propose_times", "one_time"],
  // The email after the call (migration 20270209090000): what it is for.
  recap: ["actions", "highlight", "document"],
  quote: ["price_book", "by_hand"],
};
const STEP_FIELDS: Record<string, readonly string[]> = {
  // promise_hours: the time the first reply promises a fuller answer in (migration 20270312090000).
  reply: ["mode", "due_hours", "channels", "whatsapp_template", "meeting_type", "times", "guide_url", "promise_hours"],
  // "If nothing happens" (chase) on the steps where Ogma waits for the firm (crm__chase_blocks, migration 20270219090000).
  book: ["mode", "due_hours", "meeting_type", "times", "chase"],
  meet: ["due_hours", "chase"],
  recap: ["mode", "due_hours", "document", "note", "chase_days", "chases"],
  // Customer quoting (migration 20270221090000): Qualify and Gather send a question flow, Quote picks a price book.
  qualify: ["due_hours", "flow", "chase"],
  gather: ["due_hours", "flow", "chase"],
  quote: ["mode", "due_hours", "options", "book"],
  proposal: ["due_hours", "chase"],
  accept: ["due_hours", "chase"],
  // Send a document and Make an offer (migration 20270305100000).
  send_document: ["due_hours", "document", "note", "wait_days"],
  make_offer: ["due_hours", "wait_days", "expires_days", "note", "save_offer"],
  follow_up: ["due_hours", "save_offer", "chase"],
};
const STEP_TIMES: Record<string, number> = { reply: 3, book: 4 };
const ENQUIRY_REPLY_KEY_RE = /^enquiry_reply(_[a-z0-9_]{1,26})?$/;
const OPTION_KEY_RE = /^[a-z][a-z0-9_]{0,39}$/;
const GUIDE_URL_RE = /^https:\/\/[^\s<>"']+$/;
const SAVE_OFFER_FIELDS = ["enabled", "kinds", "max_discount_pct", "approval", "approve_above_pct", "decide_after_days"];

/** A path's own save offer: any of the save offer's settings, each overriding the business's for that path. */
const stepSaveOffer: Check = (v, f) => {
  if (!plain(v)) no(f, "must be the save offer's settings");
  for (const k of Object.keys(v)) if (!SAVE_OFFER_FIELDS.includes(k)) no(`${f}.${k}`, "is not accepted here");
  const out: Record<string, unknown> = {};
  const opt = (k: string, check: Check) => {
    if (v[k] === undefined || v[k] === null) return;
    const x = check(v[k], `${f}.${k}`);
    if (!(Array.isArray(x) && x.length === 0)) out[k] = x;
  };
  opt("enabled", bool);
  opt("kinds", list(4, oneOf(SAVE_OFFER_KINDS)));
  opt("max_discount_pct", amount(0, 100));
  opt("approval", oneOf(SAVE_OFFER_APPROVAL));
  opt("approve_above_pct", amount(0, 100));
  opt("decide_after_days", int(1, 60));
  if (Object.keys(out).length === 0) no(f, "must set at least one thing");
  return out;
};

/** "If nothing happens" at a waiting step: after_hours, up to 3 chasers {gap_days, channel, brief?} and then. */
const stepChase: Check = (v, f) => {
  if (!plain(v)) no(f, "must be the step's chasers");
  for (const k of Object.keys(v)) if (!["after_hours", "steps", "then"].includes(k)) no(`${f}.${k}`, "is not accepted here");
  const out: Record<string, unknown> = {};
  if (v.after_hours !== undefined && v.after_hours !== null) out.after_hours = amount(0.25, 720)(v.after_hours, `${f}.after_hours`);
  if (v.steps !== undefined && v.steps !== null) {
    if (!Array.isArray(v.steps)) no(`${f}.steps`, "must be a list");
    if (v.steps.length > 3) no(`${f}.steps`, "may list at most 3 chasers");
    out.steps = v.steps.map((x, i) => {
      const g = `${f}.steps[${i}]`;
      if (!plain(x)) no(g, "must be a chaser");
      for (const k of Object.keys(x)) if (!["gap_days", "channel", "brief"].includes(k)) no(`${g}.${k}`, "is not accepted here");
      const c: Record<string, unknown> = { gap_days: int(0, 30)(x.gap_days, `${g}.gap_days`), channel: oneOf(["email", "whatsapp", "sms", "call_task"])(x.channel, `${g}.channel`) };
      if (x.brief !== undefined && x.brief !== null) {
        const b = text(300)(x.brief, `${g}.brief`);
        if (b !== "") c.brief = b;
      }
      return c;
    });
  }
  if (v.then !== undefined && v.then !== null) out.then = oneOf(["task", "move_on", "lost"])(v.then, `${f}.then`);
  if (Object.keys(out).length === 0) no(f, "must set at least one thing");
  return out;
};

/** One step's settings: only the fields that step takes, each in range. */
const stepEntry = (key: string): Check => (v, f) => {
  if (!plain(v)) no(f, "must be the step's settings");
  const fields = STEP_FIELDS[key] ?? ["due_hours"];
  for (const k of Object.keys(v)) if (!fields.includes(k)) no(`${f}.${k}`, "is not accepted for this step");
  const out: Record<string, unknown> = {};
  const opt = (k: string, check: Check) => {
    if (v[k] === undefined || v[k] === null) return;
    const x = check(v[k], `${f}.${k}`);
    if (!(Array.isArray(x) && x.length === 0)) out[k] = x;
  };
  opt("mode", oneOf(STEP_MODES[key] ?? []));
  opt("due_hours", amount(0.25, 720));
  opt("promise_hours", amount(0.25, 720));
  opt("times", int(1, STEP_TIMES[key] ?? 1));
  opt("meeting_type", text(41, PATH_KEY_RE, "must be a meeting type key"));
  opt("whatsapp_template", text(40, ENQUIRY_REPLY_KEY_RE, "must be enquiry_reply or a version of it (enquiry_reply_...)"));
  opt("guide_url", text(300, GUIDE_URL_RE, "must be a web address starting with https://"));
  opt("channels", list(2, oneOf(["whatsapp", "email"])));
  opt("options", list(3, text(40, OPTION_KEY_RE, "must be an option key of your price book")));
  opt("flow", text(40, OPTION_KEY_RE, "must be one of your question flows"));
  opt("book", text(40, OPTION_KEY_RE, "must be one of your price books"));
  opt("save_offer", stepSaveOffer);
  opt("document", text(41, PATH_KEY_RE, "must be quote or one of your documents"));
  opt("note", text(300));
  opt("chase_days", int(1, 30));
  opt("chases", int(0, 5));
  opt("wait_days", int(0, 60));
  opt("expires_days", int(1, 60));
  opt("chase", stepChase);
  if (out.note === "") delete out.note;
  if (Object.keys(out).length === 0) no(f, "must set at least one thing");
  return out;
};

/** A path's steps: "reply" or blocks of the path in force (its own blocks, else its template's not switched off). */
const pathSteps = (blocks: readonly string[]): Check => (v, f) => {
  if (!plain(v)) no(f, "must be the steps' settings");
  const out: Record<string, unknown> = {};
  for (const [k, x] of Object.entries(v)) {
    if (k !== "reply" && !blocks.includes(k)) no(`${f}.${k}`, "is not a step of this path");
    out[k] = stepEntry(k)(x, `${f}.${k}`);
  }
  return out;
};

/** The facts a route's condition may test (crm__fact_re) and its tests: the Quote evaluator's grammar. */
const ROUTE_FACT_RE = /^(outcome\.(result|total|option|reason)|answer\.[a-z][a-z0-9_]{0,39}|firm\.(staff|sector|country|turnover)|campaign\.(id|module))$/;
const ROUTE_OPS = ["eq", "ne", "gt", "gte", "lt", "lte", "in", "not_in", "prefix_in", "not_prefix_in", "present", "missing"];
const ROUTE_ENDS = ["end_yes", "end_lost", "reconnect", "person"];

/** A route's condition: {q, op, value} or {all}, {any} (1 to 20 parts), {not}; at most 6 deep. */
const routeCondition = (depth: number): Check => (v, f) => {
  if (depth > 6) no(f, "is nested too deeply");
  if (!plain(v)) no(f, "must be a condition");
  if ("all" in v || "any" in v || "not" in v) {
    if (Object.keys(v).length !== 1) no(f, "takes all, any or not on its own");
    if ("not" in v) return { not: routeCondition(depth + 1)(v.not, `${f}.not`) };
    const k = "all" in v ? "all" : "any";
    const parts = v[k];
    if (!Array.isArray(parts) || parts.length < 1 || parts.length > 20) no(`${f}.${k}`, "needs 1 to 20 parts");
    return { [k]: parts.map((x, i) => routeCondition(depth + 1)(x, `${f}.${k}[${i}]`)) };
  }
  for (const k of Object.keys(v)) if (!["q", "op", "value"].includes(k)) no(`${f}.${k}`, "is not accepted here");
  if (typeof v.q !== "string" || !ROUTE_FACT_RE.test(v.q)) no(`${f}.q`, "is not a fact Ogma knows");
  const op = oneOf(ROUTE_OPS)(v.op, `${f}.op`) as string;
  const num = (x: unknown) => typeof x === "number" && Number.isFinite(x);
  if (op === "present" || op === "missing") {
    if ("value" in v) no(`${f}.value`, `is not taken by ${op}`);
  } else if (["in", "not_in", "prefix_in", "not_prefix_in"].includes(op)) {
    if (!Array.isArray(v.value) || v.value.length < 1 || v.value.length > 200 || !v.value.every((x) => typeof x === "string" || num(x))) no(`${f}.value`, "must be a list of 1 to 200 values");
  } else if (["gt", "gte", "lt", "lte"].includes(op)) {
    if (!num(v.value)) no(`${f}.value`, "must be a number");
  } else if (!(typeof v.value === "string" || typeof v.value === "boolean" || num(v.value))) {
    no(`${f}.value`, "must be a value");
  }
  return v;
};

/** A path's routes (step 9.25; crm__route_problem): from a step of the path, on done or timed_out, an optional
 * condition, to a later step (or Quote again from Accept) or an end; reapproach_days only for ReConnect; at most 8
 * from a step. */
const pathRoutes = (blocks: readonly string[]): Check => (v, f) => {
  if (!Array.isArray(v)) no(f, "must be a list");
  const from: Record<string, number> = {};
  return v.map((r, i) => {
    const g = `${f}[${i}]`;
    if (!plain(r)) no(g, "must be a route");
    for (const k of Object.keys(r)) if (!["from", "on", "when", "to", "reapproach_days"].includes(k)) no(`${g}.${k}`, "is not accepted here");
    const a = oneOf(blocks)(r.from, `${g}.from`) as string;
    oneOf(["done", "timed_out"])(r.on, `${g}.on`);
    const to = oneOf([...blocks, ...ROUTE_ENDS])(r.to, `${g}.to`) as string;
    if (blocks.includes(to) && blocks.indexOf(to) <= blocks.indexOf(a) && !(a === "accept" && to === "quote")) no(g, `goes back from ${a} to ${to}: routes only go forward (except Accept back to Quote)`);
    if (r.reapproach_days !== undefined) {
      if (to !== "reconnect") no(`${g}.reapproach_days`, "is only for a route to ReConnect");
      int(1, 365)(r.reapproach_days, `${g}.reapproach_days`);
    }
    if (r.when !== undefined) routeCondition(0)(r.when, `${g}.when`);
    from[a] = (from[a] ?? 0) + 1;
    if (from[a]! > 8) no(f, `may have at most 8 routes from ${a}`);
    return r;
  });
};

/** One path: a unique key, a label, a template, the offers, flavours and campaigns that lead to it, the optional
 * blocks switched off, whether it is the default, and its step settings. The database checks the same
 * (crm__settings_check, kind "paths"). */
const pathEntry: Check = (v, f) => {
  if (!plain(v)) no(f, "must be a path");
  for (const k of Object.keys(v)) if (!["key", "label", "template", "offers", "flavours", "campaigns", "off", "default", "inbound", "blocks", "steps", "routes"].includes(k)) no(`${f}.${k}`, "is not accepted here");
  const template = oneOf(Object.keys(PATH_TEMPLATES))(v.template, `${f}.template`) as string;
  const out: Record<string, unknown> = {
    key: text(41, PATH_KEY_RE, "must be 2 to 41 lower-case letters, numbers or _, starting with a letter")(v.key, `${f}.key`),
    label: text(60, /^.{2,}$/, "must be at least 2 characters")(v.label, `${f}.label`),
    template,
  };
  const opt = (k: string, check: Check) => {
    if (v[k] === undefined || v[k] === null) return;
    out[k] = check(v[k], `${f}.${k}`);
  };
  opt("offers", list(7, oneOf(OFFERS)));
  opt("flavours", list(3, oneOf(FLAVOURS)));
  opt("campaigns", list(50, campaignId));
  opt("off", list(7, oneOf(PATH_TEMPLATES[template]!.optional)));
  opt("default", bool);
  opt("inbound", bool);
  // Steps you define (#493; crm__own_blocks, migration 20270130090000): the path's own steps in order, instead of its
  // template minus the steps switched off. Known steps, each once, at most 12, in an order the engine can follow.
  opt("blocks", (x, g) => {
    const blocks = list(12, oneOf(PATH_BLOCKS))(x, g) as string[];
    if (!blocks.length) no(g, "must list at least one step");
    const problem = pathOrderProblem(blocks);
    if (problem) no(g, problem);
    if (((out.off as string[] | undefined) ?? []).length) no(g, "cannot be given with steps switched off: leave a step out instead");
    return blocks;
  });
  const inForce = (out.blocks as string[] | undefined) ?? (PATH_TEMPLATES[template]?.blocks ?? []).filter((b) => !((out.off as string[] | undefined) ?? []).includes(b));
  // Steps must be an object when given: null, a list or text is refused (as the database's check does).
  if (v.steps !== undefined) out.steps = pathSteps(inForce)(v.steps, `${f}.steps`);
  if (v.routes !== undefined) out.routes = pathRoutes(inForce)(v.routes, `${f}.routes`);
  return out;
};

const pathList: Check = (v, f) => {
  // Up to 100 paths (crm__clean_paths, 31 Oct 2026): tens of campaigns, each with its own steps.
  const out = list(100, pathEntry)(v, f) as unknown as Record<string, unknown>[];
  const keys = new Set<string>();
  const offers = new Set<string>();
  const flavours = new Set<string>();
  const campaigns = new Set<string>();
  let defaults = 0;
  for (const p of out) {
    if (keys.has(p.key as string)) no(f, `use the key ${p.key} twice`);
    keys.add(p.key as string);
    for (const o of (p.offers as string[] | undefined) ?? []) {
      if (offers.has(o)) no(f, `send the offer ${o} to more than one path`);
      offers.add(o);
    }
    for (const fl of (p.flavours as string[] | undefined) ?? []) {
      if (flavours.has(fl)) no(f, `give the flavour ${fl} more than one path`);
      flavours.add(fl);
    }
    // Many campaigns may share a path; each campaign has one path at most.
    for (const c of (p.campaigns as string[] | undefined) ?? []) {
      if (campaigns.has(c)) no(f, `link the campaign ${c} to more than one path`);
      campaigns.add(c);
    }
    if (p.default === true) defaults += 1;
  }
  if (defaults > 1) no(f, "can have only one default path");
  if (out.filter((p) => p.inbound === true).length > 1) no(f, "can have only one Inbound path");
  return out;
};

/** Connect phase 4, booking (docs/specs/CONNECT.md section 9; migration 20261130090000): the video a meeting type uses. */
export const BOOKING_VIDEOS = ["google_meet", "teams", "zoom", "phone", "in_person", "link"] as const;
/** Connect walk-through package 6 (migration 20270101090000): the business's own video tool (scheduling.video_link). */
export const DEFAULT_VIDEOS = ["google_meet", "teams", "zoom"] as const;
const MEETING_TYPE_FIELDS = ["key", "label", "path", "minutes", "video", "location", "attendees", "buffer_before", "buffer_after",
  "notice_hours", "horizon_days", "deposit", "deposit_currency", "default"];
const ATTENDEE_RE = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;

/** Up to five extra people from the business who attend, as "a@x.com, b@y.com". */
const attendees: Check = (v, f) => {
  const s = text(400)(v, f) as string;
  if (!s) return "";
  const list = [...new Set(s.split(",").map((x) => x.trim().toLowerCase()).filter(Boolean))];
  if (list.length > 5) no(f, "may list at most 5 people");
  for (const a of list) if (!ATTENDEE_RE.test(a)) no(f, `must be email addresses separated by commas (${a.slice(0, 40)} is not one)`);
  return list.join(", ");
};

/** One meeting type: a unique key, a label, its length, video and where; buffers, notice, horizon and an optional deposit
 * (stored only: payments are phase 3). The database checks the same (crm__settings_check, kind "objects"). */
const meetingType: Check = (v, f) => {
  if (!plain(v)) no(f, "must be a meeting type");
  for (const k of Object.keys(v)) if (!MEETING_TYPE_FIELDS.includes(k)) no(`${f}.${k}`, "is not accepted here");
  const out: Record<string, unknown> = {
    key: text(41, PATH_KEY_RE, "must be 2 to 41 lower-case letters, numbers or _, starting with a letter")(v.key, `${f}.key`),
    label: text(60, /^.{2,}$/, "must be at least 2 characters")(v.label, `${f}.label`),
    minutes: int(10, 480)(v.minutes, `${f}.minutes`),
    video: oneOf(BOOKING_VIDEOS)(v.video, `${f}.video`),
  };
  const opt = (k: string, check: Check) => {
    if (v[k] === undefined || v[k] === null || v[k] === "") return;
    out[k] = check(v[k], `${f}.${k}`);
  };
  opt("path", text(41, PATH_KEY_RE, "must be a path key"));
  opt("location", text(200));
  opt("attendees", attendees);
  opt("buffer_before", int(0, 240));
  opt("buffer_after", int(0, 240));
  opt("notice_hours", int(0, 336));
  opt("horizon_days", int(1, 90));
  opt("deposit", int(0, 100_000));
  opt("deposit_currency", text(3, /^[A-Z]{3}$/, "must be a three-letter code like GBP"));
  opt("default", bool);
  if (out.video === "link" && !HTTPS_RE.test(String(out.location ?? ""))) no(`${f}.location`, "must be the meeting link, starting with https://");
  if (out.video === "in_person" && !out.location) no(`${f}.location`, "must say where you meet");
  if (out.attendees === "") delete out.attendees;
  return out;
};

const meetingTypes: Check = (v, f) => {
  if (!Array.isArray(v)) no(f, "must be a list");
  if (v.length > 10) no(f, "may list at most 10");
  const out = v.map((x, i) => meetingType(x, `${f}[${i}]`) as Record<string, unknown>);
  const keys = new Set<string>();
  let defaults = 0;
  for (const t of out) {
    if (keys.has(t.key as string)) no(f, `use the key ${t.key} twice`);
    keys.add(t.key as string);
    if (t.default === true) defaults += 1;
  }
  if (defaults > 1) no(f, "can have only one default meeting type");
  return out;
};

/** One of the business's standard documents, shared by the email after the call (Connect › Rules › Documents;
 * migration 20270209090000, crm__settings_check kind "objects"): a key, a label and an https link. */
const SHARE_DOC_RE = /^https:\/\/[^\s<>"']{4,255}$/;
const shareDocument: Check = (v, f) => {
  if (!plain(v)) no(f, "must be a document");
  for (const k of Object.keys(v)) if (!["key", "label", "url"].includes(k)) no(`${f}.${k}`, "is not accepted here");
  const url = text(263)(v.url, `${f}.url`) as string;
  if (!SHARE_DOC_RE.test(url)) no(`${f}.url`, "must be a web address starting with https://");
  return {
    key: text(41, PATH_KEY_RE, "must be 2 to 41 lower-case letters, numbers or _, starting with a letter")(v.key, `${f}.key`),
    label: text(80, /^.{2,}$/, "must be at least 2 characters")(v.label, `${f}.label`),
    url,
  };
};
const shareDocuments: Check = (v, f) => {
  if (!Array.isArray(v)) no(f, "must be a list");
  if (v.length > 20) no(f, "may list at most 20");
  const out = v.map((x, i) => shareDocument(x, `${f}[${i}]`) as Record<string, unknown>);
  const keys = new Set<string>();
  for (const d of out) {
    if (keys.has(d.key as string)) no(f, `use the key ${d.key} twice`);
    keys.add(d.key as string);
  }
  return out;
};

/** The sender of text reminders: the business's Twilio number (+44...) or messaging service (MG...), or "" for none. */
const smsSender = text(40, /^(|\+[1-9][0-9]{6,14}|MG[0-9a-fA-F]{32})$/, "must be a number like +447700900123 or a Twilio messaging service (MG...)");

// Upgrades Oct 2026 (docs/specs/UPGRADES_2026_10.md; migration 20261220010200): the choices the new sections take.
// Their forms live on the module pages, not in Settings › Your settings.
export const LETTER_TOOLS = ["scribeless", "stannp"] as const;
export const EMAIL_FINDERS = ["own", "site", "pattern", "bettercontact", "fullenrich", "hunter"] as const;
/** The finders switched on, always in Ogma's order (EMAIL_FINDERS; #358, #384): the engine tries them in that order, so a
 * custom order is refused rather than saved and ignored. */
const finders: Check = (v, f) => {
  const named = list(EMAIL_FINDERS.length, oneOf(EMAIL_FINDERS))(v, f) as string[]; // list returns the cleaned texts
  const ordered = EMAIL_FINDERS.filter((k) => named.includes(k));
  return ordered.join() === named.join() ? ordered : no(f, `must be in Ogma's order: ${EMAIL_FINDERS.join(", ")}`);
};
export const SIGNAL_PLAYS = ["director_change", "hiring", "won_work", "accounts_filed", "tender"] as const;
export const TPS_PROVIDERS = ["datasoap", "file"] as const;
/** K-04: how calls are made. tel: her own phone; twilio: through Ogma from the browser (channels.call.provider). */
export const CALL_ROUTES = ["tel", "twilio"] as const;
export const NOTETAKER_SOURCES = ["recall", "fathom", "fireflies", "paste"] as const;
export const PAYMENT_PROVIDERS = ["stripe"] as const;
export const DEPOSIT_KINDS = ["none", "percent", "fixed", "full"] as const;
export const DIGEST_CHANNELS = ["email", "whatsapp"] as const;
export const PICTURE_SOURCES = ["generated", "card"] as const;
export const AI_ANSWER_CADENCES = ["weekly", "monthly"] as const;
/** Who posts for the business as a whole (social.publisher): Buffer, Meta direct or Zernio. */
export const SOCIAL_PUBLISHERS = ["buffer", "meta", "zernio"] as const;
/** A mobile number in E.164 form (+447700900123), or "" for none. */
const mobile = text(16, /^(|\+[1-9][0-9]{6,14})$/, "must be a mobile number like +447700900123");
const domain: Check = (v, f) => text(253, HOST_RE, "must be a domain like example.com (no https://)")(typeof v === "string" ? v.toLowerCase() : v, f);

/** L-02: what drives the Find pace: new firms a week, or meetings a week. */
export const PACE_MODES = ["firms", "meetings"] as const;

/** The allowed fields per section. Keep in step with crm__settings_fields in the migration. */
export const SETTINGS_SECTIONS: Record<string, Record<string, Check>> = {
  ctas: {
    required: bool,
    default_offer: oneOf(OFFERS),
    allowed_hosts: list(20, (v, f) => text(253, HOST_RE, "must be a host name like book.example.com (no https://)")(typeof v === "string" ? v.toLowerCase() : v, f)),
    library: list(20, ctaEntry),
  },
  connect_routes: Object.fromEntries(ROUTES.map((r) => [r, firstStep])),
  connect_paths: { paths: pathList },
  // 7.8-7.12 booking (migration 20261130090000)
  connect_booking: {
    page_on: bool,
    page_url: url,
    offer_after_enquiry: bool,
    sms_reminders: bool,
    sms_sender: smsSender,
    calendly: bool,
    cal_com: bool,
    meeting_types: meetingTypes,
  },
  connect_flavours: Object.fromEntries(FLAVOURS.flatMap((fl) => [
    [`${fl}_reply_within_hours`, amount(0.25, 720)],
    [`${fl}_first_ask`, oneOf(FIRST_ASKS)],
    [`${fl}_offer_after`, oneOf(OFFER_AFTER)],
    [`${fl}_touches_before_offer`, int(0, 20)],
  ])),
  connect_save_offer: {
    enabled: bool,
    kinds: list(4, oneOf(SAVE_OFFER_KINDS)),
    max_discount_pct: amount(0, 100),
    approval: oneOf(SAVE_OFFER_APPROVAL),
    approve_above_pct: amount(0, 100),
    decide_after_days: int(1, 60),
  },
  social: { networks, cadence_per_week: int(0, 21), best_times: bestTimes, publisher: oneOf(SOCIAL_PUBLISHERS) },
  newsletter: { enabled: bool, from_name: text(100), signup_url: url, archive_url: url, syndicate: list(4, oneOf(SYNDICATE)) },
  content: {
    pillar_words: int(400, 4000),
    pillar_posts: int(0, 30),
    topics: list(30, text(80)),
    news_queries: list(20, text(120)),
    publish_to: oneOf(PUBLISH_TO),
    blog_base_url: url,
  },
  listings: { enabled: bool, niche: text(80, /^[a-z0-9_.]*$/, "must be a niche key like events.wedding_planning"), reviews_enabled: bool, review_links: reviewLinks, review_delay_days: int(0, 60), happiness_enabled: bool, happiness_delay_days: int(0, 60) },
  ads: { enabled: bool, platforms: list(5, oneOf(AD_PLATFORMS)), monthly_cap: amount(0, 1_000_000), currency: text(3, /^[A-Z]{3}$/, "must be a three-letter code like GBP"), destination: oneOf(["website", "whatsapp"]) },
  network: { referral_after_days: int(1, 365), referral_gap_days: int(7, 1095) },
  email_times: { send_window: sendWindow, min_gap_minutes: int(0, 120), send_time_test: sendTimeTest },
  speed_to_lead: {
    personal_reply_minutes: int(1, 1440),
    hours_days: list(7, oneOf(WEEKDAYS)),
    hours_start: clock,
    hours_end: clock,
    instant_reply_enabled: bool,
    instant_reply_kinds: list(3, oneOf(INSTANT_REPLY_KINDS)),
    instant_reply_channels: channelOrder,
    instant_reply_max_per_hour: int(1, 200),
    instant_reply_max_per_day: int(1, 1000),
    instant_reply_whatsapp_max_per_day: int(1, 1000),
    instant_reply_whatsapp_countries: list(20, callingCode),
    instant_reply_tally_whatsapp_field: tallyField,
    bot_check_site_key: siteKey,
    instant_reply_subject: wording(3, 200, true),
    instant_reply_in_hours: wording(10, 2000),
    instant_reply_out_of_hours: wording(10, 2000),
    new_enquiry_alert: bool,
    reply_window_start: clock,
    reply_window_end: clock,
    reply_window_days: list(7, oneOf(WEEKDAYS)),
    reply_reserve: int(0, 100),
    booking_url: url,
  },
  // Upgrades Oct 2026 (Wave 0; docs/specs/UPGRADES_2026_10.md). F-09 is the only later editor (sequence conditions).
  find_letters: { enabled: bool, tool: oneOf(LETTER_TOOLS), monthly_cap_gbp: amount(0, 2000) },
  find_contact_finders: { email_finding: finders, phone_finding: bool },
  find_plays: { ...Object.fromEntries(SIGNAL_PLAYS.map((k) => [k, bool])), heat_half_life_days: int(3, 60) },
  find_phone: { tps_provider: oneOf(TPS_PROVIDERS), recording: bool, calls_on: bool, calls_via: oneOf(CALL_ROUTES) },
  connect_notetaker: { enabled: bool, source: oneOf(NOTETAKER_SOURCES), retention_days: int(1, 30) },
  connect_payments: { provider: oneOf(PAYMENT_PROVIDERS), deposit_kind: oneOf(DEPOSIT_KINDS), deposit_percent: int(0, 100), deposit_fixed: amount(0, 100_000) },
  connect_phone_line: { enabled: bool, owner_mobile: mobile, ring_seconds: int(10, 60), text_back: bool },
  connect_quote_followups: { live_alert: bool, viewed_days: int(1, 14), unopened_days: int(1, 14), expiry_days: int(1, 14) },
  crm_interactions: { enabled: bool, add_people_from_domains: bool, keep_subject: bool, blocked_domains: list(50, domain) },
  crm_tasks: { digest: bool, digest_time: clock, digest_channel: oneOf(DIGEST_CHANNELS) },
  attract_search: { search_console_property: text(200), enabled: bool },
  attract_pictures: { sources: list(2, oneOf(PICTURE_SOURCES)), monthly_cap_usd: amount(0, 500) },
  attract_ai_answers: { competitors: list(5, text(80)), cadence: oneOf(AI_ANSWER_CADENCES) },
  newsletter_welcome: { enabled: bool, emails: int(1, 5), referral: bool },
  // K-06 Find walk-through (19 Oct 2026; migration 20261226060000): Sending rules' outreach switches and the weekly pace.
  find_channels: { email: bool, by_hand: bool, calls: bool, letters: bool, linkedin: bool, whatsapp: bool, social_dm: bool },
  // L-02 (migration 20261227020000): what drives the pace, and the meetings a week it works back from.
  find_pace: { weekly_target: int(1, 500), mode: oneOf(PACE_MODES), meetings_per_week: int(1, 50) },
  // L-01 Find › Accounts › LinkedIn (round 2, item 65; migration 20261227010000): the owner's own LinkedIn profile link.
  find_linkedin: { profile_url: url },
  // 2.21 Find › Rules › Sending rules (migration 20270225100200): the countries Ogma may email for the business; firms
  // anywhere else are contacted by hand (crm__auto_outreach_countries).
  find_countries: { countries: list(30, text(2, /^[A-Z]{2}$/, "must be two-letter country codes such as GB")) },
  // Connect walk-through package 6 (migration 20270101090000): Accounts › Video call tool, the default video tool.
  connect_video: { default_video: oneOf(DEFAULT_VIDEOS) },
  // Connect, day one (22 Oct 2026; migration 20270206090000): the hours each weekday Ogma may book, the meeting length,
  // the reminder on the calendar event, and what the meeting brief holds, in order.
  connect_hours: {
    ...Object.fromEntries(WEEKDAYS.map((d) => [d, list(3, text(11, HOUR_RANGE_RE, 'must be times like "09:00-17:00"'))])),
    meeting_minutes: int(10, 240),
    reminder_minutes: int(0, 1440),
  },
  connect_brief: { sections: list(10, oneOf(BRIEF_SECTIONS)) },
  // Sending style (22 Oct 2026; migration 20270208090000): how every email says hello and signs off, the tone and rules
  // the drafting agent follows, the words it avoids, and what it has learned from the owner's edits.
  // After the call (migration 20270209090000): the documents the email after the call can share.
  connect_documents: { documents: shareDocuments },
  // 9.28 The quote assistant's rules (Quote › Knowledge; migration 20270224090000): the kill switch, never-discuss topics,
  // questions a customer session, answer length, the daily cap, the hand-off and the model.
  quote_assistant: {
    enabled: bool,
    never: list(20, text(80, /^.{2,}$/, "must be at least 2 characters")),
    session_max: int(1, 50),
    answer_chars: int(100, 400),
    max_per_day: int(1, 3000),
    handoff: oneOf(["task", "booking", "both"]),
    model: oneOf(["haiku", "sonnet"]),
  },
  find_style: {
    sender_name: text(80),
    greeting: text(120),
    sign_off: text(300),
    tone: text(400),
    rules: list(12, text(200)),
    avoid: list(30, text(60)),
    learned_notes: list(10, text(300)),
    auto_learn: bool,
  },
};

export const MAX_CHANGE_BYTES = 40_000;

/** Checks a section change and returns it cleaned (trimmed, de-duplicated). Throws SettingsRefusal. */
export function validateSettingsChange(section: unknown, value: unknown): { section: string; value: Record<string, unknown> } {
  if (typeof section !== "string" || !Object.hasOwn(SETTINGS_SECTIONS, section)) {
    no("section", `must be one of ${Object.keys(SETTINGS_SECTIONS).join(", ")}`);
  }
  const fields = SETTINGS_SECTIONS[section]!;
  if (!plain(value)) no("value", "must be the fields to change");
  if (JSON.stringify(value).length > MAX_CHANGE_BYTES) no("value", "is too large");
  const keys = Object.keys(value);
  if (keys.length === 0) no("value", "must change at least one field");
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    if (!Object.hasOwn(fields, k)) no(`value.${k}`, `is not a ${section.replaceAll("_", " ")} setting that can be changed here`);
    out[k] = fields[k]!(value[k], k);
  }
  // Opening times must open before they close, when the change gives both (the database checks the saved result).
  for (const [start, end, what] of SPANS[section] ?? []) {
    if (typeof out[start] === "string" && typeof out[end] === "string" && (out[start] as string) >= (out[end] as string)) {
      no(start, `must be earlier than ${end.replaceAll("_", " ")} (${what})`);
    }
  }
  return { section, value: out };
}

/** Pairs of times in a section that must open before they close: [start field, end field, what they are]. */
const SPANS: Record<string, [string, string, string][]> = {
  speed_to_lead: [["hours_start", "hours_end", "working hours"], ["reply_window_start", "reply_window_end", "the reply window"]],
};

// ------------------------------------------------------------------------------------------------ 4.1 sequences
// The sequence builder (job F-09; migration 20261223050000 crm__clean_sequences checks the same, and engine/outreach.py
// STEP_CONDITIONS runs the conditions). A sequence is 1 to 10 steps {day, channel, step_id, only_if?}: from day 0, in
// day order, at most 90 days apart and at most day 365. "Stops on any reply" is not a step: a reply always ends it.
export const SEQUENCE_CHANNELS = ["email", "letter", "call", "linkedin"] as const;
/** The step conditions (only_if). verified_email is the older name of has_verified_email. */
export const SEQUENCE_CONDITIONS = ["has_verified_email", "no_verified_email", "has_phone", "has_postal_address", "no_qr_scan"] as const;
export const SEQUENCE_CONDITIONS_ACCEPTED = [...SEQUENCE_CONDITIONS, "verified_email"] as const;
/** The process step each channel's step belongs to when none is given. */
export const SEQUENCE_STEP_FOR: Record<(typeof SEQUENCE_CHANNELS)[number], string> = { email: "4.3", letter: "4.4", call: "4.14", linkedin: "4.5" };
export const SEQUENCE_MAX_STEPS = 10;
export const SEQUENCE_MAX_GAP_DAYS = 90;
export const SEQUENCE_MAX_DAY = 365;
const SEQUENCE_NAME_RE = /^[a-z][a-z0-9_]{1,40}$/;
const STEP_ID_RE = /^[0-9]{1,2}\.[0-9A-Z]{1,3}$/;

/** One sequence's steps, cleaned (step ids filled in), or a SettingsRefusal with a plain message. */
export function validateSequenceSteps(name: string, steps: unknown): Record<string, unknown>[] {
  const where = (i: number) => `Step ${i + 1} of ${name}`;
  if (!Array.isArray(steps)) no(`Sequence ${name}`, "must be a list of steps");
  if (steps.length === 0) no(`Sequence ${name}`, "needs at least one step");
  if (steps.length > SEQUENCE_MAX_STEPS) no(`Sequence ${name}`, `may have at most ${SEQUENCE_MAX_STEPS} steps`);
  let prev: number | null = null;
  return steps.map((s, i) => {
    if (!plain(s)) no(where(i), "must be a step");
    for (const k of Object.keys(s)) if (!["day", "channel", "step_id", "only_if"].includes(k)) no(`${where(i)}: ${k.slice(0, 40)}`, "is not accepted here");
    const day = s.day;
    if (typeof day !== "number" || !Number.isInteger(day) || day < 0 || day > SEQUENCE_MAX_DAY) no(`${where(i)}: the day`, `must be a whole number from 0 to ${SEQUENCE_MAX_DAY}`);
    if (i === 0 && day !== 0) no(`Sequence ${name}`, "must start on day 0");
    if (prev !== null && day < prev) no(where(i), `is on day ${day}, before step ${i} on day ${prev}: put the steps in day order`);
    if (prev !== null && day - prev > SEQUENCE_MAX_GAP_DAYS) no(where(i), `comes ${day - prev} days after step ${i}: leave at most ${SEQUENCE_MAX_GAP_DAYS} days between steps`);
    prev = day;
    const channel = s.channel;
    if (typeof channel !== "string" || !(SEQUENCE_CHANNELS as readonly string[]).includes(channel)) no(`${where(i)}: the channel`, `must be one of ${SEQUENCE_CHANNELS.join(", ")}`);
    const given = typeof s.step_id === "string" ? s.step_id.trim() : "";
    const stepId = given || SEQUENCE_STEP_FOR[channel as (typeof SEQUENCE_CHANNELS)[number]];
    if (!STEP_ID_RE.test(stepId)) no(`${where(i)}: ${stepId.slice(0, 10)}`, "is not a step id");
    const out: Record<string, unknown> = { day, channel, step_id: stepId };
    if (s.only_if !== undefined && s.only_if !== null) {
      if (typeof s.only_if !== "string" || !(SEQUENCE_CONDITIONS_ACCEPTED as readonly string[]).includes(s.only_if)) {
        no(`${where(i)}: unknown condition ${String(s.only_if).slice(0, 40)};`, `use ${SEQUENCE_CONDITIONS.join(", ")}`);
      }
      out.only_if = s.only_if;
    }
    return out;
  });
}

/** {name: steps} for propose_sequences: 1 to 10 sequences, each checked. Throws SettingsRefusal. */
export function validateSequences(value: unknown): Record<string, Record<string, unknown>[]> {
  if (!plain(value)) no("Sequences", "must be named lists of steps");
  if (JSON.stringify(value).length > MAX_CHANGE_BYTES) no("Sequences", "are too large");
  const names = Object.keys(value);
  if (names.length === 0) no("Sequences", "must include at least one sequence");
  if (names.length > 10) no("Sequences", "may change at most 10 at a time");
  const out: Record<string, Record<string, unknown>[]> = {};
  for (const n of names) {
    if (!SEQUENCE_NAME_RE.test(n)) no(`The sequence name ${n.slice(0, 41)}`, "must be 2 to 41 lower-case letters, numbers or _, starting with a letter");
    out[n] = validateSequenceSteps(n, value[n]);
  }
  return out;
}
